import React, { useState } from 'react';
import Icon from './Icon';

const Home = ({ 
  onNewTournament, 
  onLoadTournament, 
  onDeleteTournament,
  onArchiveTournament,
  tournaments, 
  onManagePlayers,
  players 
}) => {
  const [activeTab, setActiveTab] = useState('tournaments');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(null);

  const activeTournaments = tournaments.filter(t => !t.archived);
  const archivedTournaments = tournaments.filter(t => t.archived);

  const handleDelete = (tournamentId) => {
    onDeleteTournament(tournamentId);
    setShowDeleteConfirm(null);
  };

  const handleArchive = (tournamentId) => {
    onArchiveTournament(tournamentId);
    setShowArchiveConfirm(null);
  };

  const getTournamentStatus = (tournament) => {
    if (tournament.currentRound === 0) return 'Pas commencé';
    if (tournament.currentRound >= tournament.swissRounds) return 'Terminé';
    return `Round ${tournament.currentRound}/${tournament.swissRounds}`;
  };

  const canArchive = (tournament) => {
    return tournament.currentRound >= tournament.swissRounds && !tournament.archived;
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <div className="p-4 pb-0">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-red-500 mb-2">FAB Tournament</h1>
          <p className="text-gray-400 text-sm">Gestionnaire de tournois</p>
        </div>

        {/* Navigation Tabs */}
        <div className="flex bg-gray-800 rounded-lg p-1 mb-4">
          <button
            onClick={() => setActiveTab('tournaments')}
            className={`flex-1 py-3 px-4 rounded-md font-semibold transition flex items-center justify-center gap-2 ${
              activeTab === 'tournaments' 
                ? 'bg-red-600 text-white' 
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Icon d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" className="w-5 h-5" />
            Tournois
          </button>
          <button
            onClick={() => setActiveTab('players')}
            className={`flex-1 py-3 px-4 rounded-md font-semibold transition flex items-center justify-center gap-2 ${
              activeTab === 'players' 
                ? 'bg-red-600 text-white' 
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Icon d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" className="w-5 h-5" />
            Joueurs ({players.length})
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-4 pt-0">
        {activeTab === 'tournaments' ? (
          <div className="space-y-4">
            <button
              onClick={onNewTournament}
              className="w-full bg-red-600 text-white py-4 rounded-lg font-semibold active:bg-red-700 flex items-center justify-center gap-2"
            >
              <Icon d="M12 4v16m8-8H4" />
              Nouveau Tournoi
            </button>

            {activeTournaments.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold mb-3">Tournois actifs</h2>
                <div className="space-y-2">
                  {activeTournaments.map(t => (
                    <div key={t.id} className="bg-gray-800 rounded-lg overflow-hidden">
                      <button
                        onClick={() => onLoadTournament(t.id)}
                        className="w-full p-4 text-left active:bg-gray-700"
                      >
                        <div className="font-semibold">{t.name}</div>
                        <div className="text-sm text-gray-400">
                          {t.players.length} joueurs • {getTournamentStatus(t)}
                        </div>
                      </button>
                      <div className="flex border-t border-gray-700">
                        {canArchive(t) && (
                          <button
                            onClick={() => setShowArchiveConfirm(t.id)}
                            className="flex-1 py-3 text-center text-yellow-500 font-semibold border-r border-gray-700"
                          >
                            Archiver
                          </button>
                        )}
                        <button
                          onClick={() => setShowDeleteConfirm(t.id)}
                          className="flex-1 py-3 text-center text-red-500 font-semibold"
                        >
                          Supprimer
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {archivedTournaments.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold mb-3 text-gray-400">Tournois archivés</h2>
                <div className="space-y-2">
                  {archivedTournaments.map(t => (
                    <div key={t.id} className="bg-gray-800 rounded-lg overflow-hidden opacity-75">
                      <button
                        onClick={() => onLoadTournament(t.id)}
                        className="w-full p-4 text-left active:bg-gray-700"
                      >
                        <div className="font-semibold flex items-center gap-2">
                          {t.name}
                          <span className="text-xs bg-yellow-600 px-2 py-1 rounded">ARCHIVÉ</span>
                        </div>
                        <div className="text-sm text-gray-400">
                          {t.players.length} joueurs • Terminé
                        </div>
                      </button>
                      <div className="flex border-t border-gray-700">
                        <button
                          onClick={() => setShowDeleteConfirm(t.id)}
                          className="w-full py-3 text-center text-red-500 font-semibold"
                        >
                          Supprimer
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <button
              onClick={onManagePlayers}
              className="w-full bg-green-600 text-white py-4 rounded-lg font-semibold active:bg-green-700 flex items-center justify-center gap-2"
            >
              <Icon d="M12 4v16m8-8H4" />
              Gérer les Joueurs
            </button>

            {players.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold mb-3">Joueurs enregistrés</h2>
                <div className="grid grid-cols-2 gap-2">
                  {players.slice(0, 6).map(player => (
                    <div key={player.id} className="bg-gray-800 p-3 rounded-lg">
                      <div className="font-semibold text-sm">{player.name}</div>
                    </div>
                  ))}
                  {players.length > 6 && (
                    <div className="bg-gray-700 p-3 rounded-lg flex items-center justify-center">
                      <span className="text-gray-400 text-sm">+{players.length - 6} autres</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals de confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold mb-4">Confirmer la suppression</h3>
            <p className="text-gray-300 mb-6">
              Êtes-vous sûr de vouloir supprimer ce tournoi ? Cette action est irréversible.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 bg-gray-700 text-white py-3 rounded-lg font-semibold"
              >
                Annuler
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="flex-1 bg-red-600 text-white py-3 rounded-lg font-semibold"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {showArchiveConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold mb-4">Archiver le tournoi</h3>
            <p className="text-gray-300 mb-6">
              Voulez-vous archiver ce tournoi terminé ? Il sera déplacé dans la section des tournois archivés.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowArchiveConfirm(null)}
                className="flex-1 bg-gray-700 text-white py-3 rounded-lg font-semibold"
              >
                Annuler
              </button>
              <button
                onClick={() => handleArchive(showArchiveConfirm)}
                className="flex-1 bg-yellow-600 text-white py-3 rounded-lg font-semibold"
              >
                Archiver
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;